import { Component, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import AOS from 'aos';
import { RelevantTechgeekComponent } from '../relevant-techgeek/relevant-techgeek.component';
import { ResourcesService } from '../../../services/resources.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

interface ArticlePost {
  id: number;
  title: string;
  image: string;
  slug?: string;
}
@Component({
  selector: 'app-techgeek-6',
  imports: [CommonModule, RelevantTechgeekComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './techgeek-6.component.html',
  styleUrl: './techgeek-6.component.scss'
})
export class Techgeek6Component implements AfterViewInit, OnInit, OnDestroy {
  isModalOpen = false;
  isSubmitting = false;
  submitSuccess = false;
  errorMessage = '';
  downloadForm!: FormGroup;
  private jsonLdScript: any;

  readonly techGeekTitle = "Tech Geek Ed. 5";
  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }

  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {

      this.isSubmitting = true;
      this.errorMessage = '';

      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        TechGeekTitle: this.techGeekTitle
      };

      this.resourcesService.sendTechGeekEmail(payload).subscribe({
        next: (response: any) => {
          if (response && !response.IsError) {
            this.submitSuccess = true;
            this.isSubmitting = false;

            setTimeout(() => {
              this.closeModal();
            }, 4000);
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.errorMessage = "Server error. Please try again.";
        }
      });
    }
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const techGeek6Schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-6",
          "url": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-6",
          "name": "Tech Geek Ed. 6 — AI Innovations, Tech Trends & Product Spotlights",
          "description": "Explore Tech Geek Magazine featuring AI models 2026 (GPT-5.4, Claude 4.6), agentic AI, humanoid robots, PromptLock AI ransomware, and AI-powered web browsers.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".softwaredev-heading",
              ".takeaways-section .takeaway-item p",
              ".section-title"
            ]
          },
          "datePublished": "2026-04-01",
          "dateModified": "2026-04-01",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-6#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Tech Geek", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek" },
            { "@type": "ListItem", "position": 3, "name": "Tech Geek Ed. 6", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-6" }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Tech Geek Ed. 6: The Pulse of early-2026 Technology",
          "alternativeHeadline": "AI Innovations, Humanoid Workers, and Adaptive Cybersecurity — Early 2026 Edition",
          "articleBody": "Kalpita Tech Geek Magazine Edition 6 explores the AI-driven landscape across GPT-5.4, Claude 4.6, and Grok 4.20. Key spotlights include humanoid robots like Tesla Optimus, PromptLock ransomware, and AI-first browsers.",
          "author": { "@type": "Person", "name": "Kube", "url": "https://www.kalpitatechnologies.com/" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "associatedMedia": {
            "@type": "MediaObject",
            "contentUrl": "https://heyzine.com/flip-book/c5d0e85f89.html#page/1",
            "name": "Tech Geek Edition 6 — Interactive Flipbook"
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Get the Most Out of Tech Geek Magazine",
          "step": [
            { "@type": "HowToStep", "name": "Skim the Cover", "text": "Identify which of the four sections best fits your interest (AI, Trends, Spotlights, or Analysis)." },
            { "@type": "HowToStep", "name": "Deep Dive Innovations", "text": "Review the Big Four model launches and the collapse of the proprietary/open-weight quality gap." },
            { "@type": "HowToStep", "name": "Evaluate Products", "text": "Identify products like Clicks Communicator or AlterEgo that match your productivity needs." },
            { "@type": "HowToStep", "name": "Pick a Browser", "text": "Use the side-by-side benchmarking of 2026 AI browsers to optimize your web delegation." },
            { "@type": "HowToStep", "name": "Apply and Discuss", "text": "Discuss takeaways that influence your next quarter of enterprise technology adoption." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Which AI models are profiled in Edition 6?",
              "acceptedAnswer": { "@type": "Answer", "text": "Edition 6 profiles GPT-5.4, Gemini 3.1 Pro, Claude Opus 4.6, Grok 4.20, Qwen 3.5, and Kimi K2.5." }
            },
            {
              "@type": "Question",
              "name": "What is PromptLock ransomware?",
              "acceptedAnswer": { "@type": "Answer", "text": "PromptLock is the world's first confirmed AI-powered ransomware that uses real-time adaptive attacks to evade traditional security defenses." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(techGeek6Schema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  ngAfterViewInit() {
    AOS.init();
  }

  //   articles: ArticlePost[] = [
  //   {
  //     id: 1,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-one.webp'
  //   },
  //   {
  //     id: 2,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-two.webp'
  //   },
  //   {
  //     id: 3,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-three.webp'
  //   },
  //   {
  //     id: 4,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-four.webp'
  //   },
  //   {
  //     id: 5,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-five.webp'
  //   },
  //   {
  //     id: 6,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-six.webp'
  //   },
  //   {
  //     id: 7,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-seven.webp'
  //   },
  //   {
  //     id: 8,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-eight.webp'
  //   }
  // ];
  onArticleClick(article: ArticlePost): void {
    console.log('Article clicked:', article);
    // Handle navigation to article detail page
  }

  trackByArticle(index: number, article: ArticlePost): number {
    return article.id;
  }
}
